import { TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { ObstacleType } from "../Data/Obstacles";
import { PowerupData } from "../Data/PowerupData";
import { Powerups, PowerupType } from "../Data/Powerups";
import { ProjectileToken } from "../Data/ProjectileToken";
import { Regions, RegionType } from "../Data/Regions";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { Balloon } from "../GameObjects/Balloon";
import { Obstacle } from "../GameObjects/Obstacles/Obstacle";
import { OnboardingPrompt } from "../GameObjects/OnboardingPrompt";
import { ParallaxLayer } from "../GameObjects/ParallaxLayer";
import { Powerup } from "../GameObjects/Powerup";
import { TicketBooth } from "../GameObjects/TicketBooth";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";
import { WorldElement } from "../GameObjects/WorldElement";
import { Physics } from "../Physics/Physics";
import { ElementFactory } from "../Utils/ElementFactory";

enum OnboardingStepType {
    BUILDING,
    OBSTACLE,
    POWERUP,
    TICKET,
    NUM_ELEMENTS,
}

interface IOnboardingStep {
    type: OnboardingStepType;
    pos: number;
    isPrompt: boolean;
}

export class Onboarding extends ParallaxLayer {
    private static readonly PROMPTS: string[] = [
        VoiceOver.ONBOARDING_UP,
        VoiceOver.ONBOARDING_AVOID,
        VoiceOver.ONBOARDING_POWERUP,
        VoiceOver.ONBOARDING_TICKET,
    ];

    private _currentStep: number;
    private _dist: number;
    private _distAsPerc: number;
    private _distPercOffset: number;
    private _hasLiftBeenPressed: boolean;
    private _steps: IOnboardingStep[];

    private _balloon: Balloon;

    private _subscriptions: any[];
    private _voTimer: TimelineLite | undefined;

    constructor(pBalloon: Balloon) {
        super(Constants.PARALLAX_SPEED, [], new PIXI.Point(), new PIXI.Point(), new PIXI.Point());
        this._balloon = pBalloon;
        this._currentStep = 0;
        this._dist = 0;
        this._distAsPerc = 0;
        this._distPercOffset = 0;
        this._hasLiftBeenPressed = false;
        this._steps = new Array<IOnboardingStep>();
        this.initSteps();

        this._subscriptions = new Array<any>();
        this._subscriptions.push(PubSub.subscribe(Topics.BALLOON_LIFT_START, this.onLiftPressed.bind(this)));
        this._subscriptions.push(PubSub.subscribe(Topics.SPAWN_PROJECTILE, this.spawnProjectile.bind(this)));

        Application.instance.audio.load([
            VoiceOver.ONBOARDING_AVOID,
            VoiceOver.ONBOARDING_FREEPLAY,
            VoiceOver.ONBOARDING_HIT,
            VoiceOver.ONBOARDING_POWERUP,
            VoiceOver.ONBOARDING_TICKET,
            VoiceOver.ONBOARDING_UP,
            VoiceOver.POWERUP_HEALTH,
            VoiceOver.POWERUP_SHIELD,
            VoiceOver.POWERUP_SHRINK,
            VoiceOver.POWERUP_SPEED_01,
        ], HLF.AudioCategory.VO);
    }

    public get activeStep(): IOnboardingStep | undefined {
        if (this._currentStep >= this._steps.length) {
            return undefined;
        }
        else {
            return this._steps[this._currentStep];
        }
    }

    public init(pSize: PIXI.Point): void {
        this._size.copy(pSize);
        this._distPercOffset = ((pSize.x - 1024) / Constants.ONBOARDING_SIZE) / 2;
        this.moveElements(-this._distPercOffset * Constants.ONBOARDING_SIZE);
    }

    public update(pDeltaTime: number): void {
        const deltaX: number = pDeltaTime * this._speed * this._speedMod;
        this._dist += Math.abs(deltaX);
        this._distAsPerc = this._dist / Constants.ONBOARDING_SIZE;
        if (
            this.activeStep !== undefined &&
            (this._distAsPerc + this._distPercOffset) >= this.activeStep.pos
        ) {
            this.spawnStep(this._steps[this._currentStep]);
            this._currentStep++;
        }
        for (let i = 0; i < this._elements.length; ++i) {
            this._elements[i].update(pDeltaTime);
        }
        this.moveElements(deltaX);
        this.removeOffscreenElements();
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        for (let i = 0; i < this._subscriptions.length; ++i) {
            PubSub.unsubscribe(this._subscriptions[i]);
        }
        super.destroy(pOptions);
        if (this._voTimer !== undefined) { this._voTimer.kill(); }
    }

    public checkCollision(): void {
        for (let i = 0; i < this._elements.length; ++i) {
            if (Physics.instance.checkCollision(this._balloon, this._elements[i])) {
                this._elements[i].onCollision(this._balloon.invulnerable);
                if (this._elements[i] instanceof Obstacle) {
                    this._balloon.onObstacleHit();
                    PubSub.publishSync(Topics.FLASH_HEALTH_BAR, undefined);

                    Application.instance.voiceOver.play(
                        VoiceOver.ONBOARDING_HIT,
                        InteruptType.LESS,
                        VoiceCategory.INSTRUCTIONAL,
                    );
                }
                else if (this._elements[i] instanceof Powerup) {
                    this._balloon.onPowerupCollected(
                        Powerups.data.indexOf(this._elements[i].data as PowerupData),
                    );
                }
                else if (this._elements[i] instanceof TicketBooth) {
                    (this._elements[i] as TicketBooth).onTicketCollected(this._balloon.getGlobalPosition());
                }
                else if (this._elements[i] instanceof OnboardingPrompt) {
                    // Handled withing OnboardingPrompt onCollision
                }
                else if (this._elements[i] instanceof WorldElement) {
                    this._balloon.onObstacleHit();
                    PubSub.publishSync(Topics.FLASH_HEALTH_BAR, undefined);
                }
            }
        }
    }

    private initSteps(): void {
        this._steps.push({
            type: OnboardingStepType.BUILDING,
            isPrompt: false,
            pos: 0.05,
        });
        this._steps.push({
            type: OnboardingStepType.POWERUP,
            isPrompt: true,
            pos: 0.2,
        });
        this._steps.push({
            type: OnboardingStepType.OBSTACLE,
            isPrompt: false,
            pos: 0.25,
        });
        this._steps.push({
            type: OnboardingStepType.POWERUP,
            isPrompt: false,
            pos: 0.4,
        });
        this._steps.push({
            type: OnboardingStepType.TICKET,
            isPrompt: true,
            pos: 0.49,
        });
        this._steps.push({
            type: OnboardingStepType.TICKET,
            isPrompt: false,
            pos: 0.65,
        });
    }

    private spawnStep(pStep: IOnboardingStep): void {
        if (pStep.isPrompt) {
            this.spawnPrompt(pStep.type);
        }
        else {
            this.spawnVisuals(pStep.type);
        }
    }

    private spawnPrompt(pType: OnboardingStepType): void {
        const prompt: OnboardingPrompt = new OnboardingPrompt(Onboarding.PROMPTS[pType]);
        prompt.position.set(this.rightEdge, 0);
        this._elements.push(prompt);
        this.addChild(prompt);
    }

    private spawnVisuals(pStep: OnboardingStepType): void {
        switch (pStep) {
            case OnboardingStepType.BUILDING:
                this.spawnBuilding();
                break;
            case OnboardingStepType.OBSTACLE:
                this.spawnObstacle();
                break;
            case OnboardingStepType.POWERUP:
                this.spawnPowerup();
                break;
            case OnboardingStepType.TICKET:
                this.spawnTicket();
                break;
        }
    }

    private spawnBuilding(): void {
        const element: WorldElement = this.getElement();
        element.init(Regions.data[RegionType.MEXICO].generic[1]);
        element.position.set(this.rightEdge, 300);
        this._elements.push(element);
        this.addChild(element);

        this._voTimer = new TimelineLite().call(() => {
            Application.instance.voiceOver.play(
                VoiceOver.ONBOARDING_UP,
                InteruptType.EQUAL,
                VoiceCategory.INSTRUCTIONAL,
            );
            this.showLiftPrompt();
        }, undefined, this, 1.0);

    }

    private spawnObstacle(): void {
        const obstacle: Obstacle = ElementFactory.instance.makeObstacle(ObstacleType.PALETAS);
        obstacle.position.x = this.rightEdge;
        this._elements.push(obstacle);
        this.addChild(obstacle);
    }

    private spawnPowerup(): void {
        const types: PowerupType[] = [ PowerupType.INVULN, PowerupType.SPEED, PowerupType.SHRINK ];
        let powerup: Powerup;
        for (let i = 0; i < types.length; ++i) {
            powerup = new Powerup();
            powerup.init(Powerups.data[types[i]]);
            powerup.position.set(this.rightEdge, -250 + 180 * i);
            this._elements.push(powerup);
            this.addChild(powerup);
        }
    }

    private spawnTicket(): void {
        const ticketBooth: TicketBooth = new TicketBooth(true);
        ticketBooth.position.set(this.rightEdge, 300);
        this._elements.push(ticketBooth);
        this.addChild(ticketBooth);
    }

    private spawnProjectile(pTopic: string, pData: ProjectileToken): void {
        const projectile: Obstacle = ElementFactory.instance.makeObstacle(pData.type);
        projectile.position.copy(pData.pos);
        this.addChild(projectile);
        this._elements.push(projectile);

        Application.instance.voiceOver.play(
            VoiceOver.ONBOARDING_AVOID,
            InteruptType.EQUAL,
            VoiceCategory.INSTRUCTIONAL,
        );
    }

    private showLiftPrompt(): void {
        if (this._hasLiftBeenPressed === false) {
            // tslint:disable-next-line: no-string-literal
            if (Application.instance["_keyboard"]["_isActive"]) {
                PubSub.publishSync(Topics.SHOW_ONBOARDING_KEYBOARD, undefined);
            }
            else {
                PubSub.publishSync(Topics.SHOW_ONBOARDING_ARROW, undefined);
            }
        }
    }

    private onLiftPressed(pMessage: string, pData: any): void {
        if (this._hasLiftBeenPressed === false) {
            PubSub.publishSync(Topics.HIDE_ONBOARDING_ARROW, undefined);
            PubSub.publishSync(Topics.HIDE_ONBOARDING_KEYBOARD, undefined);
        }
        this._hasLiftBeenPressed = true;
    }
}
